import { useState, useCallback } from "react";
import useAuthStore from "../store/authStore";
import { addToast } from "@heroui/react";
import { useTranslation } from "react-i18next";

const getBaseUrl = () => (window as any).__CONFIG__?.apiUrl;

const refreshJwt = async (
  refreshToken: string,
  setTokens: (jwt: string, refreshToken: string) => void
): Promise<string> => {
  const response = await fetch(`${getBaseUrl()}/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) throw new Error("Failed to refresh JWT");

  const data = await response.json();
  setTokens(data.jwt, data.refreshToken);
  return data.jwt;
};

const fetchWithToken = async (
  url: string,
  options: RequestInit,
  token: string | null,
  refreshToken: string | null,
  setTokens: (jwt: string, refreshToken: string) => void,
  logout: () => void,
  t: (key: string) => string = (key) => key
) => {
  let currentToken = token;

  if (!currentToken && refreshToken) {
    currentToken = await refreshJwt(refreshToken, setTokens);
  }

  if (!currentToken) throw new Error("No token available");

  let headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
    Authorization: `Bearer ${currentToken}`,
  };

  let response = await fetch(getBaseUrl() + url, { ...options, headers });

  if (response.status === 401) {
    addToast({
      title: t("sessionExpired"),
      color: "warning",
      timeout: 5000,
    });
    logout();
  }

  if (response.status === 403) {
    addToast({
      title: t("noActiveSubscription"),
      color: "danger",
      timeout: 5000,
    });
    throw new Error("Forbidden");
  }

  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

export const useApi = () => {
  const { token, refreshToken, setTokens, logout } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  const request = useCallback(
    async (url: string, options: RequestInit = {}) => {
      setLoading(true);
      setError(null);
      try {
        return await fetchWithToken(url, options, token, refreshToken, setTokens, logout, t);
      } catch (err: any) {
        setError(err.message || "Unknown error");
        console.error(err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [token, refreshToken, setTokens, logout, t]
  );

  const get = useCallback(
    (url: string, headers?: Record<string, string>) => request(url, { method: "GET", headers }),
    [request]
  );
  const post = useCallback(
    (url: string, body?: any, headers?: Record<string, string>) =>
      request(url, { method: "POST", body: JSON.stringify(body), headers }),
    [request]
  );
  const put = useCallback(
    (url: string, body?: any, headers?: Record<string, string>) =>
      request(url, { method: "PUT", body: JSON.stringify(body), headers }),
    [request]
  );
  const del = useCallback(
    (url: string, body?: any, headers?: Record<string, string>) =>
      request(url, { method: "DELETE", body: JSON.stringify(body), headers }),
    [request]
  );

  return { loading, error, get, post, put, del };
};
